import { useState } from 'react';
import { Button, Modal, ModalFooter } from '@/components/common';
import { useResetUserPassword } from '@/hooks/useUsers';
import type { UserPasswordResetResponse, UserStatus } from '@/services/users';
import { USER_STATUS_EFFECTS, USER_STATUS_LABELS, useSetUserStatus } from './useSetUserStatus';
import { cn } from '@/utils';

/** Board #180's three states, in the order the actions are offered. */
const ALL_STATUSES: UserStatus[] = ['active', 'suspended', 'blocked'];

export const USER_STATUS_ACTION_LABELS: Record<UserStatus, string> = {
  active: 'Activate User',
  suspended: 'Suspend User',
  blocked: 'Block User',
};

/**
 * Suspend / Block / Reactivate / Reset Password for one account, with the
 * confirmation and the shown-once password dialog that go with them.
 *
 * Extracted from `UserDetailPage` so Country Admin > Brokers > Broker Admin can
 * offer the same actions without sending a country admin to `/users/...`, a
 * route they are not permitted on. One implementation, so the two screens
 * cannot drift into one confirming and the other not.
 *
 * The password is the WHOLE delivery mechanism: no email/SMS channel works on
 * this platform, and the server keeps only the hash, so closing the dialog is
 * the last time anybody can read it.
 *
 * `currentStatus` null means the payload carried no `status`; every state is
 * then offered rather than guessing which one the account is already in.
 */
export function UserAccountActions({
  userId,
  userName,
  currentStatus,
  layout = 'stack',
}: {
  userId: string;
  userName: string;
  currentStatus: UserStatus | null;
  /** `stack`: full-width column (a sidebar card). `inline`: a wrapping row. */
  layout?: 'stack' | 'inline';
}) {
  const [pendingStatus, setPendingStatus] = useState<UserStatus | null>(null);
  const [issuedCredentials, setIssuedCredentials] = useState<UserPasswordResetResponse['credentials'] | null>(
    null
  );
  const setStatusMutation = useSetUserStatus();
  const resetPasswordMutation = useResetUserPassword();

  const handleConfirmStatus = () => {
    if (!pendingStatus) return;
    setStatusMutation.mutate(
      { id: userId, status: pendingStatus },
      { onSettled: () => setPendingStatus(null) }
    );
  };

  const handleResetPassword = () => {
    resetPasswordMutation.mutate(userId, {
      onSuccess: (data) => setIssuedCredentials(data.credentials),
    });
  };

  const buttonClass = layout === 'stack' ? 'w-full justify-start' : undefined;

  return (
    <>
      <div className={cn(layout === 'stack' ? 'space-y-3' : 'flex flex-wrap gap-2')}>
        {ALL_STATUSES.filter((s) => s !== currentStatus).map((next) => (
          <Button
            key={next}
            variant="outline"
            size={layout === 'inline' ? 'sm' : undefined}
            className={buttonClass}
            onClick={() => setPendingStatus(next)}
            disabled={setStatusMutation.isPending}
          >
            {USER_STATUS_ACTION_LABELS[next]}
          </Button>
        ))}
        <Button
          variant="outline"
          size={layout === 'inline' ? 'sm' : undefined}
          className={buttonClass}
          onClick={handleResetPassword}
          isLoading={resetPasswordMutation.isPending}
        >
          Reset Password
        </Button>
      </div>

      {/* Shown-once temporary password. Mirrors the POSP `create-login` flow,
          which is the one credential-issuing mechanism already working in
          production. */}
      <Modal
        isOpen={issuedCredentials !== null}
        onClose={() => setIssuedCredentials(null)}
        title="Temporary password issued"
        description={issuedCredentials?.message}
      >
        {issuedCredentials && (
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sign in with</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {issuedCredentials.email || issuedCredentials.phone || '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Temporary password</p>
              <p className="mt-1 rounded-lg bg-gray-100 px-3 py-2 font-mono text-sm text-gray-900 break-all dark:bg-gray-700 dark:text-gray-100">
                {issuedCredentials.password}
              </p>
            </div>
            {issuedCredentials.must_change_password && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                The user will be asked to change this password on first login.
              </p>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Their saved sign-ins will stop working. A page they already have open may keep
              working for up to an hour.
            </p>
          </div>
        )}
        <ModalFooter>
          <Button onClick={() => setIssuedCredentials(null)}>Done</Button>
        </ModalFooter>
      </Modal>

      {/* Status change confirmation. Names the state, and says what it does
          to the person — "Suspend"/"Block" on their own mean nothing to
          someone deciding whether to click. */}
      <Modal
        isOpen={pendingStatus !== null}
        onClose={() => setPendingStatus(null)}
        title={pendingStatus ? `${USER_STATUS_ACTION_LABELS[pendingStatus]}?` : ''}
        description={
          pendingStatus
            ? `${userName} will be set to ${USER_STATUS_LABELS[pendingStatus]}. ${USER_STATUS_EFFECTS[pendingStatus]}`
            : undefined
        }
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setPendingStatus(null)}>
            Cancel
          </Button>
          <Button
            variant={pendingStatus === 'active' ? 'primary' : 'danger'}
            onClick={handleConfirmStatus}
            isLoading={setStatusMutation.isPending}
          >
            {pendingStatus ? USER_STATUS_ACTION_LABELS[pendingStatus] : 'Confirm'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
